<script setup lang="ts">
import { computed } from 'vue'

import Collapsible from './Collapsible.vue'

import type { UnifiedProduct } from '../../../composables/use-product-group'

const props = withDefaults(defineProps<{
  unifiedProducts?: UnifiedProduct[]
  productGroupType?: string
  isInAutocomparison?: boolean
  displayMaxItems?: number
}>(), {
  unifiedProducts: () => [],
  productGroupType: '',
  isInAutocomparison: false
})

// the-gui preview fix: uncollapsing a product card that doesn't have any pros and cons,
// still renders the collapsible component in the preview, but without any content, because
// the pros and cons are nested arrays in the `product.prosAndCons` object, which the mixin
// function `propertyExists()` that is being used to identify product's properties from the
// outer wrapper components `ParagraphTypeProductGroup.vue` and `ParagraphTypeProductAutoComparason.vue`
// does not check.
// and since is the only case, extending said function is not justified and prevents unnecessarily
// increasing its complexity.
// instead, we check for them here explicitly, and only here.
const hasProsOrCons = computed(() => props.unifiedProducts.some(({ prosAndCons }) => prosAndCons?.pros?.length || prosAndCons?.cons?.length))

const productProsAndConsStyles = (product: UnifiedProduct) => ({
  'bx-product__pros-and-cons': true,
  'bx-content__continuous-text': true,
  'bx-typo--content-continuous-text': true,
  'bx-product__pros-and-cons--single-row': !(product.prosAndCons?.pros && product.prosAndCons?.cons) && props.productGroupType === 'single'
})

const limitedItems = (items?: string[]) => items?.slice(0, props.displayMaxItems || items.length) ?? []
</script>

<template>
  <Collapsible v-if="hasProsOrCons"
               content-id="pros-and-cons"
               add-data-tc="product-pros-and-cons"
               :control-style="isInAutocomparison ? 'large' : 'small'"
               :control-border="isInAutocomparison || unifiedProducts[0].extendedInformationOrder.indexOf('prosAndCons') === 0 ? 'none' : 'top'"
               label="Vor- & Nachteile">
    <template #content>
      <template v-for="product in unifiedProducts"
                :key="product.uniqueId">
        <ul :class="productProsAndConsStyles(product)">
          <li v-if="product.prosAndCons?.pros?.length"
              :data-product-unique-id="product.uniqueId">
            <ul class="bx-product__pros">
              <li v-for="(pro, proIndex) in limitedItems(product.prosAndCons?.pros)"
                  :key="proIndex"
                  data-tc="product-pro"
                  v-html="pro" />
            </ul>
          </li>
          <li v-if="product.prosAndCons?.cons?.length"
              :data-product-unique-id="product.uniqueId">
            <ul class="bx-product__cons">
              <li v-for="(con, conIndex) in limitedItems(product.prosAndCons?.cons)"
                  :key="conIndex"
                  data-tc="product-con"
                  v-html="con" />
            </ul>
          </li>
        </ul>
      </template>
    </template>
  </Collapsible>
</template>
